import { Dropdown, TooltipV2 } from '@cimplico/components';
import { WorksheetBase, WorksheetStatusEnum, WorksheetTypeEnum } from '../../../../types/worksheet';
import { InternalCommentStatusEnum } from '../../../../types/internal-comment';
import { ClientQueryStatusEnum } from '../../../../types/client-query';
import { Account } from '../../../../types/account';
import { useState } from 'react';
import { useWorkpaperContext } from '../../../../providers/WorkpaperProvider';
import WorksheetExcludeConfirm from '../../../worksheet/WorksheetExcludeConfirm';
import WorksheetIncludeConfirm from '../../../worksheet/WorksheetIncludeConfirm';
import WorksheetTypeChangeConfirm from '../../../worksheet/WorksheetTypeChangeConfirm';
import WorksheetLinkPriorDialog from './WorksheetLinkPriorDialog';
import { isNullOrUndefined } from '../../../new-workpaper/utils';
import CimplicoStaffGuard from '../../../../components/cimplico-staff-guard/CimplicoStaffGuard';

type SecondaryActionsProps = {
    worksheet: WorksheetBase & { type: WorksheetTypeEnum; account?: Account | null };
    compactView?: boolean;
};

function SecondaryActions({ worksheet, compactView = false }: SecondaryActionsProps): JSX.Element {
    const [action, setAction] = useState<string | undefined>();

    const { readOnly, priorWorkpaperId } = useWorkpaperContext();
    const isExcluded = worksheet.status === WorksheetStatusEnum.EXCLUDED;

    function resetAction(): void {
        setAction(undefined);
    }

    function hasUnresolvedItems(worksheet: WorksheetBase): boolean {
        const activeComments = worksheet.internalComments.some((ic) => ic.status === InternalCommentStatusEnum.ACTIVE);
        const activeQueries = worksheet.clientQueries.some((cq) => cq.status !== ClientQueryStatusEnum.RESOLVED);

        if (activeComments || activeQueries) {
            return true;
        }

        return false;
    }

    return (
        <>
            {action === 'EXCLUDE' && (
                <WorksheetExcludeConfirm worksheetIds={[worksheet.id]} onClose={resetAction} onExclude={resetAction} />
            )}
            {action === 'INCLUDE' && (
                <WorksheetIncludeConfirm worksheetIds={[worksheet.id]} onClose={resetAction} onInclude={resetAction} />
            )}
            {(action === 'CHANGE_TO_GENERIC' || action === 'CHANGE_TO_RECONCILIATION') && (
                <WorksheetTypeChangeConfirm
                    newType={action === 'CHANGE_TO_RECONCILIATION' ? WorksheetTypeEnum.RECONCILIATION : WorksheetTypeEnum.GENERIC}
                    worksheetIds={[worksheet.id]}
                    onClose={resetAction}
                    onUpdate={resetAction}
                />
            )}
            {action === 'LINK_TO_PRIOR' && !isNullOrUndefined(priorWorkpaperId) && (
                <WorksheetLinkPriorDialog worksheet={worksheet} priorWorkpaperId={priorWorkpaperId} onClose={resetAction} />
            )}

            <Dropdown
                dotsDirection="horizontal"
                disabled={readOnly}
                type={compactView ? 'secondary' : undefined}
                size={compactView ? 'xs' : undefined}
            >
                {isExcluded ? (
                    <Dropdown.Option label="Include" onClick={(): void => setAction('INCLUDE')} value="include" />
                ) : (
                    <Dropdown.Option
                        label="Exclude"
                        onClick={(): void => setAction('EXCLUDE')}
                        value="exclude"
                        disabled={hasUnresolvedItems(worksheet)}
                        disabledHint={hasUnresolvedItems(worksheet) ? 'There are outstanding comments and/or queries.' : undefined}
                    />
                )}
                {!isExcluded && worksheet.account && worksheet.type === WorksheetTypeEnum.RECONCILIATION && (
                    <Dropdown.Option
                        label="Change to Generic Worksheet"
                        onClick={(): void => setAction('CHANGE_TO_GENERIC')}
                        value="changeToGeneric"
                    />
                )}
                {!isExcluded && worksheet.account && worksheet.type === WorksheetTypeEnum.GENERIC && (
                    <Dropdown.Option
                        label="Change to Reconciliation Worksheet"
                        onClick={(): void => setAction('CHANGE_TO_RECONCILIATION')}
                        value="changeToReconciliation"
                    />
                )}
                <CimplicoStaffGuard>
                    <TooltipV2
                        message={
                            !priorWorkpaperId
                                ? 'This workpaper has not been linked to a prior workpaper.'
                                : worksheet.priorWorksheetId
                                ? `This ${worksheet.account ? 'worksheet' : 'schedule'} has already been linked to a prior ${
                                      worksheet.account ? 'worksheet' : 'schedule'
                                  }`
                                : undefined
                        }
                        disabled={!worksheet.priorWorksheetId && !!priorWorkpaperId}
                    >
                        <Dropdown.Option
                            label="Link to Prior"
                            onClick={(): void => setAction('LINK_TO_PRIOR')}
                            value="linkToPrior"
                            disabled={!priorWorkpaperId || !!worksheet.priorWorksheetId}
                        />
                    </TooltipV2>
                </CimplicoStaffGuard>
            </Dropdown>
        </>
    );
}

export default SecondaryActions;
